import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, DollarSign, Calendar, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useBusinessId } from '@/hooks/useBusinessId';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatPrice } from '@/utils/currency';
import { analyticsApi } from '@/api/analytics';
import { appointmentsApi } from '@/api/appointments';

/** Format a Date as a local YYYY-MM-DD string (never via UTC toISOString). */
function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekRange(): { from: string; to: string } {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: toDateStr(monday),
    to: toDateStr(sunday),
  };
}

const DAY_LABELS: Record<number, string> = {
  0: 'Dom', 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb',
};

function StatCard({ label, value, trend, trendValue, icon: Icon }: {
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon: typeof DollarSign;
}) {
  return (
    <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '16px' }}>
      <div className="flex items-center justify-between mb-2">
        <span style={{ fontSize: '12px', color: 'var(--muted-fg)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</span>
        <Icon size={16} style={{ color: 'var(--muted-fg)' }} />
      </div>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--dark-teal)' }}>
        {value}
      </div>
      {trend && trendValue && (
        <div className="flex items-center gap-1 mt-1" style={{ fontSize: '12px' }}>
          {trend === 'up' ? (
            <TrendingUp size={14} style={{ color: '#252E3E' }} />
          ) : trend === 'down' ? (
            <TrendingDown size={14} style={{ color: '#dc2626' }} />
          ) : null}
          <span style={{ color: trend === 'up' ? '#252E3E' : trend === 'down' ? '#dc2626' : 'var(--muted-fg)' }}>
            {trendValue}
          </span>
          <span style={{ color: 'var(--muted-fg)' }}>vs semana anterior</span>
        </div>
      )}
    </div>
  );
}

export function Insights() {
  const businessId = useBusinessId();
  const currency = useBusinessCurrency();
  const { from, to } = useMemo(getWeekRange, []);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['analytics', 'summary', businessId, from, to],
    queryFn: () => analyticsApi.getSummary(businessId, from, to),
    enabled: !!businessId,
    staleTime: 5 * 60_000,
  });

  const { data: weekAppointments = [] } = useQuery({
    queryKey: ['appointments', 'week', businessId, from, to],
    queryFn: () => appointmentsApi.search(businessId, { appointmentDateFrom: from, appointmentDateTo: to }),
    enabled: !!businessId,
    staleTime: 5 * 60_000,
  });

  // Build daily chart data from real appointments
  const dailyData = useMemo(() => {
    const byDay: Record<number, { count: number; revenue: number }> = {};
    for (let d = 0; d < 7; d++) byDay[d] = { count: 0, revenue: 0 };
    for (const apt of weekAppointments) {
      if (apt.stateCode === 'CANCELLED' || apt.stateCode === 'NO_SHOW' || apt.stateCode === 'EXPIRED') continue;
      const date = new Date(apt.appointmentDate + 'T12:00:00');
      const day = date.getDay();
      byDay[day] = byDay[day] ?? { count: 0, revenue: 0 };
      byDay[day].count++;
      const totalPrice = apt.services?.reduce((sum, s) => sum + (s.price ?? 0), 0) ?? 0;
      byDay[day].revenue += totalPrice;
    }
    return [1, 2, 3, 4, 5, 6, 0].map(d => ({
      day: DAY_LABELS[d],
      appointments: byDay[d].count,
      revenue: byDay[d].revenue,
    }));
  }, [weekAppointments]);

  // Build service breakdown from real appointments
  const serviceData = useMemo(() => {
    const byService: Record<string, { count: number; revenue: number }> = {};
    for (const apt of weekAppointments) {
      if (apt.stateCode === 'CANCELLED' || apt.stateCode === 'NO_SHOW' || apt.stateCode === 'EXPIRED') continue;
      const name = apt.serviceName || 'Otro';
      if (!byService[name]) byService[name] = { count: 0, revenue: 0 };
      byService[name].count++;
      const totalPrice = apt.services?.reduce((sum, s) => sum + (s.price ?? 0), 0) ?? 0;
      byService[name].revenue += totalPrice;
    }
    return Object.entries(byService)
      .map(([service, data]) => ({ service, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [weekAppointments]);

  if (summaryLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-fg)' }} />
      </div>
    );
  }

  const revTrend = summary?.revenueTrend;
  const aptTrend = summary?.appointmentsTrend;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto" style={{ paddingTop: '16px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>

        <div className="mb-4">
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>
            Dashboard
          </span>
          <span style={{ fontSize: '13px', color: 'var(--muted-fg)', marginLeft: '8px' }}>
            Esta semana
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard
            label="Citas"
            value={summary?.totalAppointments ?? 0}
            icon={Calendar}
            trend={aptTrend ? (aptTrend.positive ? 'up' : 'down') : undefined}
            trendValue={aptTrend ? `${aptTrend.percentage}%` : undefined}
          />
          <StatCard
            label="Ingresos"
            value={formatPrice(summary?.totalRevenue ?? 0, currency)}
            icon={DollarSign}
            trend={revTrend ? (revTrend.positive ? 'up' : 'down') : undefined}
            trendValue={revTrend ? `${revTrend.percentage}%` : undefined}
          />
          <StatCard
            label="Ticket promedio"
            value={formatPrice(summary?.avgTicket ?? 0, currency)}
            icon={DollarSign}
          />
          <StatCard
            label="Tasa completadas"
            value={weekAppointments.length > 0
              ? `${Math.round(weekAppointments.filter(a => a.stateCode === 'COMPLETED').length / weekAppointments.length * 100)}%`
              : '—'}
            icon={Calendar}
          />
        </div>

        {/* Weekly Chart */}
        <div className="rounded-xl mb-6" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-teal)', marginBottom: '12px' }}>
            Citas por día
          </div>
          <div style={{ height: '180px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E8E4E0', fontSize: '12px' }}
                  formatter={(value: number) => [value, 'Citas']}
                />
                <Bar dataKey="appointments" radius={[4, 4, 0, 0]}>
                  {dailyData.map((_, i) => (
                    <Cell key={i} fill={i === new Date().getDay() - 1 ? '#E07B54' : '#243E3D'} fillOpacity={i === new Date().getDay() - 1 ? 1 : 0.6} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Breakdown */}
        {serviceData.length > 0 && (
          <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-teal)', marginBottom: '12px' }}>
              Servicios más solicitados
            </div>
            <div className="space-y-3">
              {serviceData.map((s, i) => {
                const maxCount = serviceData[0]?.count ?? 1;
                return (
                  <div key={s.service} className="flex items-center gap-3">
                    <span style={{ fontSize: '12px', color: 'var(--muted-fg)', width: '20px', textAlign: 'right' }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="truncate min-w-0" style={{ fontSize: '13px', color: 'var(--dark-teal)', fontWeight: 500 }}>{s.service}</span>
                        <span className="flex-shrink-0" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{s.count} citas</span>
                      </div>
                      <div className="rounded-full overflow-hidden" style={{ height: '4px', backgroundColor: 'var(--border-color)' }}>
                        <div className="rounded-full" style={{ height: '100%', width: `${(s.count / maxCount) * 100}%`, backgroundColor: i === 0 ? '#E07B54' : '#243E3D', opacity: i === 0 ? 1 : 0.5 }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
